import {IRecommendedOffer} from "../../offer/types/IRecommendedOffer";
import {IRecommendedProperty} from "../../property/types/IRecommendedProperty";
import {ApplicationModalStep} from "../types/ApplicationModalStep";
import {IApplicationData} from "../types/IApplicationData";
import {IApplicationModalStepState} from "../types/IApplicationModalStepState";
import {IApplicationNotificationConsentStepPayload} from "../types/IApplicationNotificationConsentStepPayload";
import {IApplicationMultiLeadStepPayload} from "../types/multi_lead/IApplicationMultiLeadStepPayload";
import {IApplicationMultiLeadSummaryStepState} from "../types/multi_lead/IApplicationMultiLeadSummaryStepState";
import {IApplicationReducerData} from "./application_reducer";

export enum ModalStepActionTypes {
    NEXT_STEP = "nextStep",
    CLOSE = "close",
    SET_IS_AUTORESPONDER_ENABLED = "setIsAutoresponderEnabled",
    OPEN = "open",
    UPDATE_LEAD_DATA = "updateLeadData",
    UPDATE_SINGLE_LEAD_RESPONSE_DATA = "updateSingleLeadResponse",
    SET_MULTILEAD_STEP = "setMultileadStep",
    SET_NOTIFICATION_CONSENT_STEP = "setNotificationConsentStep",
    SET_GOODBYE_INFO_STEP = "setGoodbyeInfoStep",
    SET_CLOSE = "setClose"
}

export interface IApplicationSentPayload {
    originalApplicationResponse?: IApplicationData | null;
    recommendedOffers?: IRecommendedProperty[] | IRecommendedOffer[] | null;
    recommendationsWithPriceFilter: boolean;
    crossPromoData?: ICrossPromoData | null;
}

interface IRecommendationsSendPayload {
    originalApplicationResponse?: IApplicationData | null;
    appliedRecommendedOffers?: number[];
}

interface IIsAutoresponderEnabledPayload {
    isAutoresponderEnabled: boolean;
}

type IPayload =
    | IApplicationSentPayload
    | IRecommendationsSendPayload
    | IIsAutoresponderEnabledPayload
    | IUpdateLeadDataAction
    | IApplicationMultiLeadSummaryStepState
    | IApplicationMultiLeadStepPayload
    | IApplicationNotificationConsentStepPayload
    | undefined;

interface INextStepAction {
    type: ModalStepActionTypes.NEXT_STEP;
    payload?: IPayload;
}

interface IOpenAction {
    type: ModalStepActionTypes.OPEN;
    payload?: {
        step?: ApplicationModalStep;
    };
}

interface ICloseAction {
    type: ModalStepActionTypes.CLOSE;
}

interface ISetIsAutoresponderEnabledAction {
    type: ModalStepActionTypes.SET_IS_AUTORESPONDER_ENABLED;
    payload: IIsAutoresponderEnabledPayload;
}

interface IUpdateLeadDataAction {
    type: ModalStepActionTypes.UPDATE_LEAD_DATA;
    payload: IApplicationReducerData;
}

interface IUpdateSingleLeadResponseAction {
    type: ModalStepActionTypes.UPDATE_SINGLE_LEAD_RESPONSE_DATA;
    payload: {
        originalApplicationResponse: IApplicationData | null;
    };
}

interface ISetMultiLeadStepAction {
    type: ModalStepActionTypes.SET_MULTILEAD_STEP;
    payload: IApplicationMultiLeadStepPayload;
}

interface ISetNotificationConsentStepAction {
    type: ModalStepActionTypes.SET_NOTIFICATION_CONSENT_STEP;
    payload: IApplicationNotificationConsentStepPayload;
}

interface ISetGoodbyeInfoStepAction {
    type: ModalStepActionTypes.SET_GOODBYE_INFO_STEP;
}

interface ISetCloseAction {
    type: ModalStepActionTypes.SET_CLOSE;
}

export const initialStepState: IApplicationModalStepState = {
    step: ApplicationModalStep.WAITING,
    originalApplicationResponse: null,
    recommendedOffers: null,
    appliedRecommendedOffers: [],
    multiLeadSummaryItems: [],
    multiLeadProperty: null,
    shouldCloseOnOverlayClick: true,
    isAutoresponderEnabled: false,
    crossPromoData: {
        getHomeOffersCount: 0,
        geoPoint: "",
        locationName: ""
    },
    recommendationsWithPriceFilter: false,
    leadData: null
};

const CLOSING_STATE = {
    ...initialStepState,
    step: ApplicationModalStep.CLOSE
};

export interface ICrossPromoData {
    getHomeOffersCount: number;
    geoPoint: string;
    roomsNumber?: number[];
    locationName: string;
    isProperty?: boolean;
}

type IModalStepAction =
    | INextStepAction
    | ICloseAction
    | ISetIsAutoresponderEnabledAction
    | IOpenAction
    | IUpdateLeadDataAction
    | IUpdateSingleLeadResponseAction
    | ISetMultiLeadStepAction
    | ISetNotificationConsentStepAction
    | ISetGoodbyeInfoStepAction
    | ISetCloseAction;

export const applicationModalStepReducer = (state: IApplicationModalStepState, action: IModalStepAction): IApplicationModalStepState => {
    switch (action.type) {
        case ModalStepActionTypes.NEXT_STEP: {
            return nextAfterStep(state, action.payload);
        }
        case ModalStepActionTypes.OPEN: {
            const stepToOpen = action.payload?.step || ApplicationModalStep.APPLICATION_FORM_STANDARD;
            return {...state, step: stepToOpen};
        }
        case ModalStepActionTypes.CLOSE: {
            return closeAfterStep(state);
        }
        case ModalStepActionTypes.SET_IS_AUTORESPONDER_ENABLED: {
            return {
                ...state,
                isAutoresponderEnabled: action.payload.isAutoresponderEnabled
            };
        }
        case ModalStepActionTypes.UPDATE_LEAD_DATA: {
            return {
                ...state,
                leadData: action.payload
            };
        }
        case ModalStepActionTypes.UPDATE_SINGLE_LEAD_RESPONSE_DATA: {
            return {
                ...state,
                originalApplicationResponse: action.payload.originalApplicationResponse
            };
        }
        case ModalStepActionTypes.SET_MULTILEAD_STEP: {
            return {
                ...state,
                step: ApplicationModalStep.MULTILEAD
            };
        }
        case ModalStepActionTypes.SET_NOTIFICATION_CONSENT_STEP: {
            return {
                ...state,
                step: ApplicationModalStep.NOTIFICATION_CONSENT,
                originalApplicationResponse: action.payload.originalApplicationResponse
            };
        }
        case ModalStepActionTypes.SET_GOODBYE_INFO_STEP: {
            return {
                ...state,
                step: ApplicationModalStep.GOODBYE_INFO
            };
        }
        case ModalStepActionTypes.SET_CLOSE:
        default: {
            return CLOSING_STATE;
        }
    }
};

const nextAfterStep = (state: IApplicationModalStepState, payload: IPayload) => {
    switch (state.step) {
        case ApplicationModalStep.MULTILEAD: {
            if (
                payload &&
                "originalApplicationResponse" in payload &&
                !payload.originalApplicationResponse?.marketing_email &&
                !payload.originalApplicationResponse?.marketing_sms
            ) {
                return {
                    ...state,
                    originalApplicationResponse: payload.originalApplicationResponse as IApplicationData,
                    step: ApplicationModalStep.NOTIFICATION_CONSENT
                };
            }

            return CLOSING_STATE;
        }

        default: {
            return CLOSING_STATE;
        }
    }
};

const closeAfterStep = (state: IApplicationModalStepState) => {
    switch (state.step) {
        case ApplicationModalStep.APPLICATION_FORM_STANDARD: {
            if (state.isAutoresponderEnabled || state.forceClose) {
                return CLOSING_STATE;
            }

            return {
                ...state,
                step: ApplicationModalStep.GOODBYE_INFO
            };
        }
        default: {
            return CLOSING_STATE;
        }
    }
};
import {IRecommendedOffer} from "../../offer/types/IRecommendedOffer";
import {IRecommendedProperty} from "../../property/types/IRecommendedProperty";
import {ApplicationModalStep} from "../types/ApplicationModalStep";
import {IApplicationData} from "../types/IApplicationData";
import {IApplicationModalStepState} from "../types/IApplicationModalStepState";
import {IApplicationNotificationConsentStepPayload} from "../types/IApplicationNotificationConsentStepPayload";
import {IApplicationMultiLeadStepPayload} from "../types/multi_lead/IApplicationMultiLeadStepPayload";
import {IApplicationMultiLeadSummaryStepState} from "../types/multi_lead/IApplicationMultiLeadSummaryStepState";
import {IApplicationReducerData} from "./application_reducer";

export enum ModalStepActionTypes {
    NEXT_STEP = "nextStep",
    CLOSE = "close",
    SET_IS_AUTORESPONDER_ENABLED = "setIsAutoresponderEnabled",
    OPEN = "open",
    UPDATE_LEAD_DATA = "updateLeadData",
    UPDATE_SINGLE_LEAD_RESPONSE_DATA = "updateSingleLeadResponse",
    SET_MULTILEAD_STEP = "setMultileadStep",
    SET_NOTIFICATION_CONSENT_STEP = "setNotificationConsentStep",
    SET_GOODBYE_INFO_STEP = "setGoodbyeInfoStep",
    SET_CLOSE = "setClose"
}

export interface IApplicationSentPayload {
    originalApplicationResponse?: IApplicationData | null;
    recommendedOffers?: IRecommendedProperty[] | IRecommendedOffer[] | null;
    recommendationsWithPriceFilter: boolean;
    crossPromoData?: ICrossPromoData | null;
}

interface IRecommendationsSendPayload {
    originalApplicationResponse?: IApplicationData | null;
    appliedRecommendedOffers?: number[];
}

interface IIsAutoresponderEnabledPayload {
    isAutoresponderEnabled: boolean;
}

type IPayload =
    | IApplicationSentPayload
    | IRecommendationsSendPayload
    | IIsAutoresponderEnabledPayload
    | IUpdateLeadDataAction
    | IApplicationMultiLeadSummaryStepState
    | IApplicationMultiLeadStepPayload
    | IApplicationNotificationConsentStepPayload
    | undefined;

interface INextStepAction {
    type: ModalStepActionTypes.NEXT_STEP;
    payload?: IPayload;
}

interface IOpenAction {
    type: ModalStepActionTypes.OPEN;
    payload?: {
        step?: ApplicationModalStep;
    };
}

interface ICloseAction {
    type: ModalStepActionTypes.CLOSE;
}

interface ISetIsAutoresponderEnabledAction {
    type: ModalStepActionTypes.SET_IS_AUTORESPONDER_ENABLED;
    payload: IIsAutoresponderEnabledPayload;
}

interface IUpdateLeadDataAction {
    type: ModalStepActionTypes.UPDATE_LEAD_DATA;
    payload: IApplicationReducerData;
}

interface IUpdateSingleLeadResponseAction {
    type: ModalStepActionTypes.UPDATE_SINGLE_LEAD_RESPONSE_DATA;
    payload: {
        originalApplicationResponse: IApplicationData | null;
    };
}

interface ISetMultiLeadStepAction {
    type: ModalStepActionTypes.SET_MULTILEAD_STEP;
    payload: IApplicationMultiLeadStepPayload;
}

interface ISetNotificationConsentStepAction {
    type: ModalStepActionTypes.SET_NOTIFICATION_CONSENT_STEP;
    payload: IApplicationNotificationConsentStepPayload;
}

interface ISetGoodbyeInfoStepAction {
    type: ModalStepActionTypes.SET_GOODBYE_INFO_STEP;
}

interface ISetCloseAction {
    type: ModalStepActionTypes.SET_CLOSE;
}

export const initialStepState: IApplicationModalStepState = {
    step: ApplicationModalStep.WAITING,
    originalApplicationResponse: null,
    recommendedOffers: null,
    appliedRecommendedOffers: [],
    multiLeadSummaryItems: [],
    multiLeadProperty: null,
    shouldCloseOnOverlayClick: true,
    isAutoresponderEnabled: false,
    crossPromoData: {
        getHomeOffersCount: 0,
        geoPoint: "",
        locationName: ""
    },
    recommendationsWithPriceFilter: false,
    leadData: null
};

const CLOSING_STATE = {
    ...initialStepState,
    step: ApplicationModalStep.CLOSE
};

export interface ICrossPromoData {
    getHomeOffersCount: number;
    geoPoint: string;
    roomsNumber?: number[];
    locationName: string;
    isProperty?: boolean;
}

type IModalStepAction =
    | INextStepAction
    | ICloseAction
    | ISetIsAutoresponderEnabledAction
    | IOpenAction
    | IUpdateLeadDataAction
    | IUpdateSingleLeadResponseAction
    | ISetMultiLeadStepAction
    | ISetNotificationConsentStepAction
    | ISetGoodbyeInfoStepAction
    | ISetCloseAction;

export const applicationModalStepReducer = (state: IApplicationModalStepState, action: IModalStepAction): IApplicationModalStepState => {
    switch (action.type) {
        case ModalStepActionTypes.NEXT_STEP: {
            return nextAfterStep(state, action.payload);
        }
        case ModalStepActionTypes.OPEN: {
            const stepToOpen = action.payload?.step || ApplicationModalStep.APPLICATION_FORM_STANDARD;
            return {...state, step: stepToOpen};
        }
        case ModalStepActionTypes.CLOSE: {
            return closeAfterStep(state);
        }
        case ModalStepActionTypes.SET_IS_AUTORESPONDER_ENABLED: {
            return {
                ...state,
                isAutoresponderEnabled: action.payload.isAutoresponderEnabled
            };
        }
        case ModalStepActionTypes.UPDATE_LEAD_DATA: {
            return {
                ...state,
                leadData: action.payload
            };
        }
        case ModalStepActionTypes.UPDATE_SINGLE_LEAD_RESPONSE_DATA: {
            return {
                ...state,
                originalApplicationResponse: action.payload.originalApplicationResponse
            };
        }
        case ModalStepActionTypes.SET_MULTILEAD_STEP: {
            return {
                ...state,
                step: ApplicationModalStep.MULTILEAD
            };
        }
        case ModalStepActionTypes.SET_NOTIFICATION_CONSENT_STEP: {
            return {
                ...state,
                step: ApplicationModalStep.NOTIFICATION_CONSENT,
                originalApplicationResponse: action.payload.originalApplicationResponse
            };
        }
        case ModalStepActionTypes.SET_GOODBYE_INFO_STEP: {
            return {
                ...state,
                step: ApplicationModalStep.GOODBYE_INFO
            };
        }
        case ModalStepActionTypes.SET_CLOSE:
        default: {
            return CLOSING_STATE;
        }
    }
};

const nextAfterStep = (state: IApplicationModalStepState, payload: IPayload) => {
    switch (state.step) {
        case ApplicationModalStep.MULTILEAD: {
            if (
                payload &&
                "originalApplicationResponse" in payload &&
                !payload.originalApplicationResponse?.marketing_email &&
                !payload.originalApplicationResponse?.marketing_sms
            ) {
                return {
                    ...state,
                    originalApplicationResponse: payload.originalApplicationResponse as IApplicationData,
                    step: ApplicationModalStep.NOTIFICATION_CONSENT
                };
            }

            return CLOSING_STATE;
        }

        default: {
            return CLOSING_STATE;
        }
    }
};

const closeAfterStep = (state: IApplicationModalStepState) => {
    switch (state.step) {
        case ApplicationModalStep.APPLICATION_FORM_STANDARD: {
            if (state.isAutoresponderEnabled || state.forceClose) {
                return CLOSING_STATE;
            }

            return {
                ...state,
                step: ApplicationModalStep.GOODBYE_INFO
            };
        }
        default: {
            return CLOSING_STATE;
        }
    }
};
